/**
 * テーマ切替（システム / ライト / ダーク）。
 *
 * 記憶する値（`ThemePreference`）は 3 択。実際に描画へ効くのは「解決後の
 * light / dark」の 2 値で、`system` のときは OS の `prefers-color-scheme` を
 * 読んで解決する。解決結果は `<html>` の `.dark` クラスと `color-scheme`
 * スタイルへ反映する（CSS 側は `:root.dark` のみを見る — global.css 参照）。
 *
 * FOUC 防止のため、初期適用は index.html のインライン `<script>`（本ファイルと
 * 同じロジックのミニ版）がモジュール読込より前に一度走らせる。本ファイルは
 * その後の「ユーザー操作による切替」と「system 選択中の OS 変化への追従」を担う。
 */

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "bloc.theme";

const PREFERENCES: readonly ThemePreference[] = ["system", "light", "dark"];

function isThemePreference(v: unknown): v is ThemePreference {
  return typeof v === "string" && (PREFERENCES as readonly string[]).includes(v);
}

/** localStorage から現在の設定を読む（未設定・不正値は `system`）。 */
export function getThemePreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemePreference(raw)) return raw;
  } catch {
    // localStorage 不可（プライベートモード等）→ 既定の system。
  }
  return "system";
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** 設定を OS 状態で解決して実効テーマ（light / dark）を返す。 */
export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref;
}

/** `<html>` へ実効テーマを適用する（クラス + color-scheme）。 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  // color-scheme はフォーム部品・スクロールバー等のネイティブ描画に効く。
  // dark-OS で light を強制した場合などにここを固定しないとズレる。
  root.style.colorScheme = resolved;
}

const listeners = new Set<() => void>();

/** 設定変更・OS 追従の両方で発火する購読（useSyncExternalStore 用）。 */
export function subscribeTheme(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * 現在の実効テーマ（light / dark）。sonner の `theme` など「解決後の 2 値」を
 * 必要とする消費者向けの snapshot（useSyncExternalStore と組で使う）。
 */
export function getResolvedTheme(): ResolvedTheme {
  return resolveTheme(getThemePreference());
}

function emit(): void {
  for (const cb of listeners) cb();
}

/** ユーザー操作による設定変更。保存 → 適用 → 購読者へ通知。 */
export function setThemePreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // 保存不可でもセッション内の見た目だけは切り替える。
  }
  applyResolvedTheme(resolveTheme(pref));
  emit();
}

let osWatchStarted = false;

/**
 * OS の配色変化と他タブでの設定変更の監視を一度だけ開始する。アプリ起動時に
 * 一度呼ぶ。
 * - OS 変化: `system` 選択中のときだけ再適用（＋購読者へ通知）。
 * - `storage` イベント: 別タブで setThemePreference された値へ追従する
 *   （テーマは視覚差が大きく、タブ間で食い違うと壊れて見えるため）。
 */
export function startOsThemeWatch(): void {
  if (osWatchStarted) return;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return;
  }
  osWatchStarted = true;
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", () => {
    if (getThemePreference() === "system") {
      applyResolvedTheme(resolveTheme("system"));
    }
    // system 以外でも、サブメニューの「システム」ラベル等が OS 値を映すため通知。
    emit();
  });
  window.addEventListener("storage", (e) => {
    // 他タブの localStorage 書き込みのみ発火する（自タブでは発火しない）。
    if (e.key !== null && e.key !== THEME_STORAGE_KEY) return;
    applyResolvedTheme(getResolvedTheme());
    emit();
  });
}
